
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    
import React, { useRef } from "react";
import {
  getPathsFromContext,
  getResource,
  getResourceCollection,
  getResourceFromContext,
} from "next-drupal";
import NodePageContent from "components/Page/NodePageContent";
import Layout from "components/Layout";
import PageHeader from "components/PageHeader";
import Meta from "components/Meta";
import NodePageMenuItem from "components/Page/NodePageMenuItem";
import Form from "components/Form";
import getForm from "lib/getForm";
import getParams from "lib/getParams";
import getCrumbs from "lib/getCrumbs";
import { getGlobals } from "lib/getGlobals";
import ContactCTABlock from "components/Block/ContactCTABlock";
import { DrupalJsonApiParams } from "drupal-jsonapi-params";
import TopRightShape from "components/Shapes/TopRightShape";
import useIsInViewport from "hook/useIsInViewport";
import filterPathsSlug from "lib/filterPathsSlug";
import useTranslation from "next-translate/useTranslation";
import getChilds from "lib/getChilds";

export default function NodePage({
  crumbs,
  globals,
  node,
  form,
  childs,
  blocks,
}) {
  const ref = useRef(null);
  const isInViewport = useIsInViewport(ref);
  const { lang } = useTranslation();

  if (!node) return null;

  const hasForm = form.id !== null && form.data !== null;
  const hasChilds =
    childs.length > 0 &&
    childs.filter((child) => child.langcode === lang).length > 0;
  const transitionDuration = 0.5;
  const transitionDelay = transitionDuration * 0.75;

  return (
    <Layout globals={globals}>
      <Meta
        title={node.field_meta_title || node.title}
        description={node.field_meta_description || null}
        useTemplate
      />

      <PageHeader
        crumbs={[...crumbs, { title: node.title }]}
        title={node.title}
        image={node.field_image}
      />

      <section className="relative pt-16 pb-24">
        <div className="absolute top-0 right-0" aria-hidden="true">
          <TopRightShape className="w-[14.78vw] h-auto text-primary/30" />
        </div>

        <div
          ref={ref}
          className="container mx-auto px-4 transition-all"
          style={{
            opacity: isInViewport ? 100 : 0,
            transitionDuration: `${transitionDuration}s`,
            transitionDelay: `${transitionDelay}s`,
          }}
        >
          <NodePageContent node={node} />
        </div>
      </section>

      {hasForm && (
        <section className="pt-16 pb-24">
          <div
            className="container mx-auto px-4 transition-all"
            style={{
              opacity: isInViewport ? 100 : 0,
              transitionDuration: `${transitionDuration}s`,
              transitionDelay: `${transitionDelay}s`,
            }}
          >
            <div className="mx-auto lg:max-w-screen-lg">
              <Form id={form.id} dataFields={form.data} />
            </div>
          </div>
        </section>
      )}

      {blocks?.contactCta && (
        <section className="pt-16 pb-24">
          <div
            className="container mx-auto px-4 transition-all"
            style={{
              opacity: isInViewport ? 100 : 0,
              transitionDuration: `${transitionDuration}s`,
              transitionDelay: `${transitionDelay}s`,
            }}
          >
            <ContactCTABlock data={blocks.contactCta} />
          </div>
        </section>
      )}

      {hasChilds && (
        <section className="relative pt-16 pb-24 bg-secondary-light">
          <div className="container mx-auto px-4 transition-all">
            <div className="lg:max-w-screen-lg mx-auto">
              <h2 className="font-important text-4xl mb-12">{node.title}</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {childs.map((item, index) => {
                  if (item.langcode !== lang) return null;
                  const thisTransitionDelay = transitionDelay * index;

                  return (
                    <div
                      key={item.id}
                      className="transition-all"
                      style={{
                        opacity: isInViewport ? 100 : 0,
                        transform: `translateX(${
                          isInViewport ? 0 : "-1.5rem"
                        })`,
                        transitionDuration: `${transitionDuration}s`,
                        transitionDelay: `${thisTransitionDelay}s`,
                      }}
                    >
                      <NodePageMenuItem item={item} />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}
    </Layout>
  );
}

export async function getStaticPaths(context) {
  const paths = await getPathsFromContext(["node--page"], context);

  return {
    paths: filterPathsSlug(paths, "slug"),
    fallback: "blocking",
  };
}

 async function _getStaticProps(context) {
  const node = await getResourceFromContext("node--page", context, {
    params: getParams("node--page"),
  });

  if (!node || (!context.preview && !node?.status)) {
    return {
      notFound: true,
      revalidate: 30,
    };
  }

  const globals = await getGlobals(context);

  // Get node corresponding menu item
  const menuItem = globals.menus.crumbsFallback.filter((item) =>
    item.url.includes(node.path.alias)
  )[0];

  // Get crumbs & childs from menu item
  const crumbs = menuItem
    ? getCrumbs(menuItem, globals.menus.crumbsFallback)
    : [];
  const childs = menuItem
    ? getChilds(menuItem, globals.menus.crumbsFallback)
    : [];

  // Get childs nodes IDs to get images...
  const nodesIds = childs.reduce((result, child) => {
    if (child.route && child.route.parameters && child.route.parameters.node) {
      result.push(child.route.parameters.node);
    }

    return result;
  }, []);

  if (nodesIds.length > 0) {
    const params = new DrupalJsonApiParams();
    params
      .addInclude(["field_image"])
      .addFilter("drupal_internal__nid", nodesIds, "IN")
      .addFilter("status", 1)
      .addFilter("langcode", context.locale);

    const nodes = await getResourceCollection("node--page", {
      params: params.getQueryObject(),
      locale: context.locale,
      defaultLocale: context.defaultLocale,
    });

    if (nodes.length) {
      childs.map((child) => {
        const newChild = child;
        if (
          child.route &&
          child.route.parameters &&
          child.route.parameters.node
        ) {
          newChild.node =
            nodes.filter(
              (el) =>
                parseInt(el.drupal_internal__nid, 10) ===
                parseInt(child.route.parameters.node, 10)
            )[0] || null;
        }

        return newChild;
      });
    }
  }

  // Get node webform
  const formId = node.field_form
    ? node.field_form.resourceIdObjMeta.drupal_internal__target_id
    : null;
  const formData = formId ? await getForm(formId) : null;

  return {
    props: {
      globals,
      node,
      crumbs,
      childs,
      form: {
        id: formId || null,
        data: formData || null,
      },
      blocks: {
        contactCta: await getResource(
          "block_content--links",
          "1b7a2d3f-55a0-4c22-b354-1f5a4148f19c",
          {
            locale: context.locale,
            defaultLocale: context.defaultLocale,
          }
        ),
      },
    },
    revalidate: 30,
  };
}


    export async function getStaticProps(ctx) {
        let res = _getStaticProps(ctx)
        if(typeof res.then === 'function') res = await res
        return {
          
          ...res,
          props: {
            ...(res.props || {}),
            ...(await __loadNamespaces({
              ...ctx,
              pathname: '/[...slug]',
              loaderName: 'getStaticProps',
              ...__i18nConfig,
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
            }))
          }
        }
    }
  